import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { PageHeader } from "../../components/HeaderComponents";
import { BreadCrumbsGeneral } from "../../components/NavigationComponents";
import { ContentContext } from "../../contexts/ContentContext";
import { formatTimestamp } from "../../utils";
import { requestMyWarnings } from "../../requests/warning_requests";
import {
    WarningDetails,
    WarningIcon,
    WarningTypeBadge,
    ticketLink,
    warningStatusStyle,
    warningStatuses,
    warningSummary,
    warningTypes,
} from "../../components/WarningComponents";
import { categoryColor, dayEnd, dayStart } from "./warnings";

const PAGE_SIZE = 25;
// Keyword warnings belong to no employee, so they never show up here.
const OWN_TYPES = warningTypes.filter((type) => type !== "keyword");

// The logged-in employee's own warnings, read-only. Employees with the warnings page use that one.
export const MyWarningsPage = () => {
    const { t } = useTranslation("common");
    const { openErrorModal } = useContext(ContentContext);
    const [filters, setFilters] = useState({ type: "", status: "", dateFrom: "", dateTo: "" });
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ warnings: [], total: 0 });
    const [loading, setLoading] = useState(false);

    const load = () => {
        setLoading(true);
        requestMyWarnings({
            type: filters.type,
            status: filters.status,
            dateFrom: dayStart(filters.dateFrom),
            dateTo: dayEnd(filters.dateTo),
            page,
            pageSize: PAGE_SIZE,
        })
            .then(setData)
            .catch((error) => {
                console.error(error.status, error.message);
                openErrorModal(error);
            })
            .finally(() => setLoading(false));
    };

    useEffect(load, [filters, page]);

    const setFilter = (key, value) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const nPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    const selectClass = "select select-sm select-bordered bg-white dark:bg-gray-700";
    const breadCrumbs = [
        { link: "/admin/dashboard", name: t("Home") },
        { link: null, name: t("warning.my_title") },
    ];

    return (
        <main className="flex flex-col justify-start items-center w-full py-2 gap-5 pb-10 px-4 h-full">
            <PageHeader pageTitle={t("warning.my_title")} />
            <div className="flex w-full justify-between">
                <div className="flex flex-col flex-1">
                    <h1 className="text-xl font-semibold flex items-center gap-2"><WarningIcon size={20} /> {t("warning.my_title")}</h1>
                    <BreadCrumbsGeneral elements={breadCrumbs} />
                    <span className="text-sm opacity-70 mt-1">{t("warning.my_help")}</span>
                </div>
            </div>
            <div className="flex flex-col w-full bg-white dark:bg-gray-700 rounded-xl shadow-lg py-4 gap-3">
                <div className="flex flex-wrap items-end gap-3 px-4">
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_type")}
                        <select className={selectClass} value={filters.type} onChange={(e) => setFilter("type", e.target.value)}>
                            <option value="">{t("All")}</option>
                            {OWN_TYPES.map((type) => <option key={type} value={type}>{t(`warning.type.${type}`)}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_status")}
                        <select className={selectClass} value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
                            <option value="">{t("All")}</option>
                            {warningStatuses.map((s) => <option key={s} value={s}>{t(`warning.status.${s}`)}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_from")}
                        <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={filters.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} />
                    </label>
                    <label className="flex flex-col text-xs gap-1">
                        {t("warning.filter_to")}
                        <input type="date" className="input input-sm input-bordered bg-white dark:bg-gray-700" value={filters.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} />
                    </label>
                    <button className="btn btn-sm" onClick={load} disabled={loading}>{t("warning.refresh")}</button>
                </div>

                <div className="overflow-x-auto w-full">
                    <table className="table table-sm w-full">
                        <thead>
                            <tr>
                                <th>{t("warning.col_date")}</th>
                                <th>{t("warning.col_type")}</th>
                                <th>{t("warning.col_ticket")}</th>
                                <th>{t("warning.col_category")}</th>
                                <th className="min-w-72">{t("warning.col_detail")}</th>
                                <th>{t("warning.col_status")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.warnings.map((w) => {
                                const [time, date] = formatTimestamp(w.timestamp * 1000).split(" ");
                                return (
                                    <tr key={w.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40">
                                        <td className="whitespace-nowrap">
                                            <div className="flex flex-col leading-tight">
                                                <span>{time}</span>
                                                <span className="text-xs opacity-60">{date}</span>
                                            </div>
                                        </td>
                                        <td><WarningTypeBadge type={w.type} /></td>
                                        <td className="whitespace-nowrap">
                                            {w.ticket_code ? (
                                                <NavLink className="text-primary dark:text-blue-400 underline" to={ticketLink(w.ticket_code)}>#{w.ticket_created_at}</NavLink>
                                            ) : "-"}
                                        </td>
                                        <td className="whitespace-nowrap">
                                            {w.ticket_category
                                                ? <span className="badge border-none font-medium whitespace-nowrap" style={{ backgroundColor: categoryColor(w.ticket_category)[0], color: categoryColor(w.ticket_category)[1] }}>{w.ticket_category}</span>
                                                : <span className="opacity-40">-</span>}
                                        </td>
                                        <td>
                                            <div className="flex flex-col gap-1">
                                                <span className="font-medium text-gray-800 dark:text-gray-100">{warningSummary(t, w, "employee")}</span>
                                                <WarningDetails warning={w} />
                                            </div>
                                        </td>
                                        <td><span className={`badge whitespace-nowrap ${warningStatusStyle[w.status]}`}>{t(`warning.status.${w.status}`)}</span></td>
                                    </tr>
                                );
                            })}
                            {data.warnings.length === 0 && (
                                <tr><td colSpan={6} className="text-center italic opacity-70 py-6">{loading ? "…" : t("warning.my_empty")}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-center gap-3 px-4">
                    <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                    <span className="text-sm">{page} / {nPages} · {t("warning.total", { count: data.total })}</span>
                    <button className="btn btn-sm" disabled={page >= nPages} onClick={() => setPage(page + 1)}>›</button>
                </div>
            </div>
        </main>
    );
};
